/**
 * Multi-Destination Shipping Module
 * Manages address destinations, cart item assignment, and shipping rate generation.
 *
 * Every unit's destination is written to its cart line as a `_Count-N` property,
 * and every destination gets one MULTI-DEST-SHIPPING line carrying the same
 * address. The backend splits the order into one sub-order per destination by
 * matching the two, so a unit whose `_Count-N` is missing or names an address
 * with no shipping line never ships.
 *
 * #79391 (2026-09-28) lost 18 of 19 units that way:
 *   - the same wine sat on two cart lines, and every destination change copied
 *     the FIRST line's properties (lookup by variant id) onto whichever line
 *     was being edited, while overlapping requests overwrote each other;
 *   - the $0 shipping variant did not exist, the add failed silently, and the
 *     free destination got no shipping line.
 * So cart writes are now serialised and addressed by line index, checkout
 * rewrites every unit from the page and replaces all shipping lines in one
 * add, and nothing leaves the page until the cart has been read back.
 */

const MultiDestinationShipping = (function () {
  'use strict';

  /* US States lookup for validation */
  const US_STATES = {
    'Arizona': 'AZ', 'Alabama': 'AL', 'Alaska': 'AK', 'Arkansas': 'AR',
    'California': 'CA', 'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE',
    'District of Columbia': 'DC', 'Florida': 'FL', 'Georgia': 'GA', 'Hawaii': 'HI',
    'Idaho': 'ID', 'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA',
    'Kansas': 'KS', 'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME',
    'Maryland': 'MD', 'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN',
    'Mississippi': 'MS', 'Missouri': 'MO', 'Montana': 'MT', 'Nebraska': 'NE',
    'Nevada': 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ', 'New Mexico': 'NM',
    'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND',
    'Ohio': 'OH', 'Oklahoma': 'OK', 'Oregon': 'OR', 'Pennsylvania': 'PA',
    'Rhode Island': 'RI', 'South Carolina': 'SC', 'South Dakota': 'SD',
    'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT', 'Vermont': 'VT',
    'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV',
    'Wisconsin': 'WI', 'Wyoming': 'WY',
    'AZ': 'AZ', 'AL': 'AL', 'AK': 'AK', 'AR': 'AR', 'CA': 'CA', 'CO': 'CO',
    'CT': 'CT', 'DE': 'DE', 'DC': 'DC', 'FL': 'FL', 'GA': 'GA', 'HI': 'HI',
    'ID': 'ID', 'IL': 'IL', 'IN': 'IN', 'IA': 'IA', 'KS': 'KS', 'KY': 'KY',
    'LA': 'LA', 'ME': 'ME', 'MD': 'MD', 'MA': 'MA', 'MI': 'MI', 'MN': 'MN',
    'MS': 'MS', 'MO': 'MO', 'MT': 'MT', 'NE': 'NE', 'NV': 'NV', 'NH': 'NH',
    'NJ': 'NJ', 'NM': 'NM', 'NY': 'NY', 'NC': 'NC', 'ND': 'ND', 'OH': 'OH',
    'OK': 'OK', 'OR': 'OR', 'PA': 'PA', 'RI': 'RI', 'SC': 'SC', 'SD': 'SD',
    'TN': 'TN', 'TX': 'TX', 'UT': 'UT', 'VT': 'VT', 'VA': 'VA', 'WA': 'WA',
    'WV': 'WV', 'WI': 'WI', 'WY': 'WY'
  };

  const STATE_CODE_BY_LOWER = {};
  Object.keys(US_STATES).forEach(function (state) {
    STATE_CODE_BY_LOWER[state.toLowerCase()] = US_STATES[state];
  });

  const DESTINATION_SKU = 'MULTI-DEST-SHIPPING';
  const PLACEHOLDER = 'Choose a destination';

  let config = {};
  let addressList = [];

  // Cart writes run one at a time, in order.
  let writeChain = Promise.resolve();
  // Set once checkout has added lines: the render-time line indices no longer
  // match the cart, so selection changes stop writing to it (checkout rewrites
  // every unit anyway).
  let lineIndicesStale = false;
  // What the rendered rates were generated from: one group per destination,
  // and the value of every unit select at that moment.
  let rateSnapshot = null;
  let generatingRates = false;
  let checkingOut = false;

  /**
   * Initialize the module
   * @param {object} options - configuration
   */
  function init(options) {
    config = Object.assign({
      apiEndpoint: '',
      batchSize: 10,
      enableGiftNotes: true,
      enableStateValidation: true,
      shippingVariantId: 43736716378282,
      freeShippingVariantId: '',
      excludeVariantId: 43736716378282,
      sectionId: ''
    }, options);

    // The render-time line of each unit, kept for grouping units by line after
    // data-line-item-count has been moved to a fresh index.
    unitSelects().forEach(function (select) {
      select.dataset.mdsLine = select.dataset.lineItemCount;
    });

    setupModal();
    setupDestinationChangeListeners();
    setupGenerateRatesListener();
    restorePreviousAddresses();
  }

  function unitSelects() {
    return Array.prototype.slice.call(document.querySelectorAll('.mds-destination-select'));
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * Compared the way the backend compares addresses: case and spacing ignored,
   * and HTML entities decoded in case the cart hands a value back escaped.
   */
  function addressMatchKey(address) {
    return String(address || '')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
  }

  function isDestinationLine(item) {
    var variantId = String(item.variant_id);
    return item.sku === DESTINATION_SKU ||
      variantId === String(config.shippingVariantId) ||
      (config.freeShippingVariantId !== '' && variantId === String(config.freeShippingVariantId));
  }

  function queueCartWrite(task) {
    var run = writeChain.then(task);
    writeChain = run.catch(function () {});
    return run;
  }

  /** fetch a cart endpoint; a non-2xx answer throws with Shopify's own message. */
  async function cartRequest(url, body) {
    var options = { credentials: 'same-origin', headers: { Accept: 'application/json' } };
    if (body !== undefined) {
      options.method = 'POST';
      options.headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify(body);
    }
    var res = await fetch(url, options);
    var data = null;
    try {
      data = await res.json();
    } catch (e) {
      data = null;
    }
    if (!res.ok) {
      var message = data && (data.description || data.message);
      throw new Error(message || 'The cart could not be updated (' + res.status + ').');
    }
    return data;
  }

  function getCart() {
    return cartRequest('/cart.js');
  }

  /**
   * Restore addresses from cart item properties on load
   */
  function restorePreviousAddresses() {
    var selects = unitSelects();
    selects.forEach(function (select) {
      var prevVal = select.dataset.previousValue;
      if (prevVal && prevVal.length > 0) {
        if (!addressList.includes(prevVal)) {
          addressList.push(prevVal);
        }
      }
    });

    if (addressList.length > 0) {
      populateAllSelects();
      // Restore selected values
      selects.forEach(function (select) {
        if (select.dataset.previousValue) {
          select.value = select.dataset.previousValue;
        }
      });
    }
  }

  /**
   * Set up the modal open/close behavior
   */
  function setupModal() {
    var modal = document.getElementById('mds-modal-' + config.sectionId);
    var openBtn = document.getElementById('mds-add-destination-btn-' + config.sectionId);
    var closeBtn = document.getElementById('mds-modal-close-' + config.sectionId);
    var form = document.getElementById('mds-destination-form-' + config.sectionId);

    if (!modal || !openBtn) return;

    openBtn.addEventListener('click', function () {
      modal.classList.add('mds-modal--visible');
    });

    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        modal.classList.remove('mds-modal--visible');
      });
    }

    // Close on backdrop click
    modal.addEventListener('click', function (e) {
      if (e.target === modal) {
        modal.classList.remove('mds-modal--visible');
      }
    });

    // Form submit
    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        handleDestinationSubmit();
      });
    }
  }

  /** A field value safe to put in the pipe-separated address. */
  function field(id) {
    var el = document.getElementById(id + config.sectionId);
    return el ? el.value.replace(/\|/g, '/').replace(/\s+/g, ' ').trim() : '';
  }

  /**
   * Handle destination form submission
   */
  function handleDestinationSubmit() {
    var name = field('mds-name-');
    var address1 = field('mds-address1-');
    var address2 = field('mds-address2-');
    var company = field('mds-company-');
    var city = field('mds-city-');
    var state = field('mds-state-');
    var zip = field('mds-zip-');
    var phone = field('mds-phone-');
    var errEl = document.getElementById('mds-form-errors-' + config.sectionId);

    var stateCode = STATE_CODE_BY_LOWER[state.toLowerCase()];

    // Validate
    var errors = [];
    if (name.length === 0) errors.push('Please add a name.');
    if (address1.length <= 4) errors.push('Please add an address.');
    if (city.length <= 2) errors.push('Please add a city.');
    if (config.enableStateValidation) {
      if (!stateCode) errors.push('Please add a valid US state.');
    } else {
      if (state.length <= 1) errors.push('Please add a state.');
    }
    if (zip.length !== 5) errors.push('Please add a 5-digit zip code.');
    if (phone.length < 10) errors.push('Please add a phone number (10+ digits).');

    if (errors.length > 0) {
      if (errEl) {
        errEl.style.display = 'flex';
        errEl.innerHTML = errors.map(function (e) { return '<div>' + escapeHtml(e) + '</div>'; }).join('');
      }
      return;
    }

    // Build address string. The state is stored as its code, the form the
    // rates API echoes back.
    var addressFull = [
      name, address1, address2, company, city, stateCode || state, zip, phone
    ].join(' | ');

    if (!addressList.includes(addressFull)) {
      addressList.push(addressFull);
    }

    populateAllSelects();

    // Reset form and close modal
    if (errEl) {
      errEl.style.display = 'none';
      errEl.innerHTML = '';
    }

    var form = document.getElementById('mds-destination-form-' + config.sectionId);
    if (form) form.reset();

    var modal = document.getElementById('mds-modal-' + config.sectionId);
    if (modal) modal.classList.remove('mds-modal--visible');
  }

  /**
   * What a destination option shows: name, street and city, or every
   * non-empty part when `full`. Display only; the option value stays the
   * full pipe-separated address.
   */
  function destinationLabel(addr, full) {
    var parts = String(addr).split('|').map(function (s) { return s.trim(); });
    var shown = full ? parts : [parts[0], parts[1], parts[4]];
    return shown.filter(Boolean).join(', ') || String(addr);
  }

  /**
   * Populate all destination select elements with current address list
   */
  function populateAllSelects() {
    var labels = addressList.map(function (addr) { return destinationLabel(addr, false); });
    // Two addresses that read the same in short form are shown in full.
    labels = labels.map(function (label, i) {
      return labels.indexOf(label) === labels.lastIndexOf(label) ? label : destinationLabel(addressList[i], true);
    });

    unitSelects().forEach(function (select) {
      var currentValue = select.value;

      // Remove all options except the first default
      while (select.options.length > 1) {
        select.remove(1);
      }

      // Add all addresses
      addressList.forEach(function (addr, i) {
        var option = document.createElement('option');
        option.textContent = labels[i];
        option.value = addr;
        select.appendChild(option);
      });

      // Restore previous selection
      if (currentValue && currentValue !== PLACEHOLDER) {
        select.value = currentValue;
      }
    });
  }

  /**
   * Keep each unit's destination on its cart line, so a reload restores the
   * page. Addressed by line index, checked against the variant, one write at
   * a time. Checkout rewrites every unit regardless, so a skipped write here
   * only costs the restore.
   */
  function setupDestinationChangeListeners() {
    document.addEventListener('change', function (e) {
      var target = e.target;
      if (!target.classList || !target.classList.contains('mds-destination-select')) return;
      if (target.value === PLACEHOLDER) return;

      var value = target.value;
      var line = parseInt(target.dataset.lineItemCount, 10);
      var lineNum = target.dataset.lineNum;
      var variantId = String(target.dataset.variantId);

      queueCartWrite(async function () {
        if (lineIndicesStale) return;
        var cart = await getCart();
        var item = cart.items[line - 1];
        if (!item || String(item.variant_id) !== variantId) {
          lineIndicesStale = true;
          return;
        }
        var props = Object.assign({}, item.properties || {});
        props['_Count-' + lineNum] = value;
        // Unique per line, so two lines of the same wine can never end up with
        // identical properties and be merged by Shopify under the page.
        props._mds_line = String(line);
        var updated = await cartRequest('/cart/change.js', {
          line: line,
          quantity: item.quantity,
          properties: props
        });
        if (!updated || updated.items.length !== cart.items.length) lineIndicesStale = true;
      }).catch(function (err) {
        console.error('[MDS] Cart update error:', err);
      });
    });
  }

  /**
   * Set up generate shipping rates button
   */
  function setupGenerateRatesListener() {
    var generateBtn = document.getElementById('mds-generate-rates-' + config.sectionId);
    if (!generateBtn) return;

    generateBtn.addEventListener('click', async function () {
      if (generatingRates) return;
      generatingRates = true;
      generateBtn.disabled = true;

      var spinner = document.getElementById('mds-rate-spinner-' + config.sectionId);
      var errorMsg = document.getElementById('mds-rate-error-' + config.sectionId);
      var ratesDiv = document.getElementById('mds-generated-rates-' + config.sectionId);

      if (errorMsg) errorMsg.style.display = 'none';
      if (spinner) spinner.style.display = 'inline-block';

      try {
        await generateRates(errorMsg, ratesDiv);
      } finally {
        if (spinner) spinner.style.display = 'none';
        generateBtn.disabled = false;
        generatingRates = false;
      }
    });
  }

  function showRateError(errorMsg, message) {
    if (!errorMsg) return;
    errorMsg.textContent = message;
    errorMsg.style.display = 'block';
  }

  async function generateRates(errorMsg, ratesDiv) {
    var selects = unitSelects();
    var productAddressMap = {};

    rateSnapshot = null;
    if (ratesDiv) ratesDiv.innerHTML = '';

    if (selects.length === 0) {
      showRateError(errorMsg, 'Your cart is empty.');
      return;
    }

    for (var i = 0; i < selects.length; i++) {
      var curr = selects[i];
      var currValue = curr.value;

      if (!currValue || currValue === PLACEHOLDER) {
        showRateError(errorMsg, 'Must select a destination for each item above.');
        return;
      }

      var variantId = curr.dataset.variantId;
      if (!productAddressMap[currValue]) {
        productAddressMap[currValue] = {};
      }
      if (!productAddressMap[currValue][variantId]) {
        productAddressMap[currValue][variantId] = {
          totalGrams: 0,
          quantity: 0,
          title: curr.dataset.lineTitle,
          price: curr.dataset.linePrice
        };
      }
      productAddressMap[currValue][variantId].quantity += 1;
      productAddressMap[currValue][variantId].totalGrams += parseInt(curr.dataset.lineGrams, 10) || 0;
    }

    var values = selects.map(function (s) { return s.value; });
    // The rates come back in the order the destinations were sent.
    var sources = Object.keys(productAddressMap);

    var batchSize = config.batchSize || 10;
    var allRates = [];
    var requestFailed = false;

    for (var b = 0; b < sources.length; b += batchSize) {
      var batchMap = {};
      sources.slice(b, b + batchSize).forEach(function (address) {
        batchMap[address] = productAddressMap[address];
      });
      try {
        var response = await fetch(config.apiEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(batchMap)
        });
        var batchRates = await response.json();
        if (!response.ok || !Array.isArray(batchRates.answer)) {
          requestFailed = true;
          break;
        }
        allRates = allRates.concat(batchRates.answer);
      } catch (error) {
        console.error('[MDS] Rate batch error:', error);
        requestFailed = true;
        break;
      }
    }

    if (requestFailed || allRates.length !== sources.length) {
      showRateError(errorMsg, 'Shipping rates could not be generated. Please try again.');
      return;
    }

    var groups = [];
    var failed = [];
    for (var r = 0; r < allRates.length; r++) {
      var row = allRates[r];
      var dest = (row.address && row.address.destination) || {};
      var sourceParts = sources[r].split('|').map(function (s) { return s.trim(); });
      if (addressMatchKey(dest.name) !== addressMatchKey(sourceParts[0])) {
        // The answer is not for the destination sent in this position.
        showRateError(errorMsg, 'Shipping rates could not be generated. Please try again.');
        return;
      }
      var rates = Array.isArray(row.rates) ? row.rates : [];
      if (rates.length === 0) {
        failed.push(dest);
        continue;
      }
      groups.push({
        source: sources[r],
        destination: dest,
        canonical: canonicalAddress(dest, sourceParts),
        rates: rates,
        itemCount: values.filter(function (v) { return v === sources[r]; }).length
      });
    }

    if (failed.length > 0) {
      renderRateErrors(ratesDiv, failed);
      return;
    }

    var seen = {};
    for (var g = 0; g < groups.length; g++) {
      var key = addressMatchKey(groups[g].canonical);
      if (seen[key]) {
        showRateError(errorMsg, 'Two destinations are the same address (' + groups[g].destination.name +
          '). Please send those items to one destination and generate rates again.');
        return;
      }
      seen[key] = true;
    }

    rateSnapshot = { values: values, groups: groups };
    renderRates(ratesDiv, groups);
  }

  /**
   * The address both the unit properties and the shipping line carry: the
   * destination as the rates API echoed it, so the two can never disagree.
   */
  function canonicalAddress(dest, sourceParts) {
    return [
      dest.name,
      dest.address1,
      dest.address2 || '',
      dest.company_name || '',
      dest.city,
      dest.province || sourceParts[5] || '',
      dest.postal_code,
      dest.phone || ''
    ].map(function (part) { return String(part == null ? '' : part).trim(); }).join(' | ');
  }

  /** The address as display lines; empty parts are left out. */
  function destinationHtml(addr) {
    function part(value) {
      return String(value == null ? '' : value).trim();
    }
    var street = [part(addr.address1), part(addr.address2)].filter(Boolean).join(', ');
    var region = [part(addr.province), part(addr.postal_code)].filter(Boolean).join(' ');
    var place = [part(addr.city), region].filter(Boolean).join(', ');
    var html = '<span class="mds-destination__name">' + escapeHtml(part(addr.name)) + '</span>';
    [part(addr.company_name), street, place, part(addr.phone)].forEach(function (line) {
      if (line) html += '<span>' + escapeHtml(line) + '</span>';
    });
    return '<address class="mds-destination">' + html + '</address>';
  }

  // The theme's select chevron (snippets/icon.liquid, "dropdown-chevron").
  var CHEVRON_SVG = '<svg aria-hidden="true" focusable="false" fill="none" width="10" viewBox="0 0 10 6">' +
    '<path d="m1 1 4 4 4-4" stroke="currentColor" stroke-linecap="square"/></svg>';

  /**
   * Render shipping rate results
   */
  function renderRates(container, groups) {
    if (!container) return;
    var html = '';

    for (var index = 0; index < groups.length; index++) {
      var group = groups[index];
      html += '<div id="mds-rate-group-' + index + '" class="mds-rate-group">';
      html += '<div class="mds-rate-group__destination">';
      html += destinationHtml(group.destination);
      html += '<span class="mds-rate-group__count">' + group.itemCount + (group.itemCount === 1 ? ' item' : ' items') + '</span>';
      html += '</div>';
      html += '<div class="mds-rate-group__fields">';
      html += '<div class="mds-field">';
      html += '<label class="mds-field__label" for="mds-group-select-' + index + '">Shipping</label>';
      html += '<div class="form-control">';
      html += '<select id="mds-group-select-' + index + '" class="select mds-rate-option" data-group-index="' + index + '">';

      for (var ri = 0; ri < group.rates.length; ri++) {
        var rate = group.rates[ri];
        var dollars = Math.ceil(parseFloat(rate.total_price) / 100);
        var label = rate.service_name + ': ' + dollars.toFixed(2);
        html += '<option value="' + dollars + '">' + escapeHtml(label) + '</option>';
      }

      html += '</select>' + CHEVRON_SVG + '</div></div>';

      if (config.enableGiftNotes) {
        html += '<div class="mds-field">';
        html += '<label class="mds-field__label" for="mds-gift-note-' + index + '">Gift note</label>';
        html += '<textarea id="mds-gift-note-' + index + '" class="textarea mds-gift-note-textarea" rows="4" maxlength="500"></textarea>';
        html += '</div>';
      }

      html += '</div></div>';
    }

    // The visible button runs handleCheckout; the hidden form is what it
    // submits once the cart has been written and read back.
    html += '<div class="mds-checkout">' +
      '<button type="button" id="mds-checkout-btn-' + config.sectionId + '" class="button mds-checkout__btn">' +
      'Checkout<div id="mds-checkout-spinner-' + config.sectionId + '" class="mds-spinner"><div></div><div></div><div></div><div></div></div>' +
      '</button>' +
      '<div id="mds-checkout-error-' + config.sectionId + '" class="mds-error-message mds-checkout__error" role="alert"></div>' +
      '</div>' +
      '<form action="/cart" method="POST" novalidate hidden>' +
      '<button type="submit" id="mds-checkout-live-' + config.sectionId + '" name="checkout" class="button">Checkout</button>' +
      '</form>';

    container.innerHTML = html;

    var checkoutBtn = document.getElementById('mds-checkout-btn-' + config.sectionId);
    if (checkoutBtn) {
      checkoutBtn.addEventListener('click', function () {
        handleCheckout(checkoutBtn);
      });
    }
  }

  function renderRateErrors(container, destinations) {
    if (!container) return;
    var html = '';
    for (var i = 0; i < destinations.length; i++) {
      html += '<div class="mds-rate-group">';
      html += '<div class="mds-rate-group__destination">' + destinationHtml(destinations[i]) + '</div>';
      html += '<p class="mds-rate-group__error" role="alert">No shipping rates for this address. Check it and generate rates again.</p>';
      html += '</div>';
    }
    container.innerHTML = html;
  }

  function setCheckoutError(message) {
    var el = document.getElementById('mds-checkout-error-' + config.sectionId);
    if (!el) return;
    el.textContent = message || '';
    el.style.display = message ? 'block' : 'none';
  }

  /** Units grouped by their render-time cart line, in page order. */
  function pageLines(selects) {
    var lines = [];
    var byLine = {};
    selects.forEach(function (select) {
      var key = select.dataset.mdsLine;
      if (!byLine[key]) {
        byLine[key] = { variantId: String(select.dataset.variantId), selects: [] };
        lines.push(byLine[key]);
      }
      byLine[key].selects.push(select);
    });
    return lines;
  }

  /**
   * Write every unit's destination and the shipping lines, read the cart
   * back, and only then go to checkout. Any failure stops on this page with a
   * message; nothing is submitted half-written.
   */
  async function handleCheckout(checkoutBtn) {
    if (checkingOut) return;
    checkingOut = true;
    checkoutBtn.disabled = true;
    var spinner = document.getElementById('mds-checkout-spinner-' + config.sectionId);
    if (spinner) spinner.style.display = 'inline-block';
    setCheckoutError('');

    var leaving = false;
    try {
      await writeChain;

      var selects = unitSelects();
      var changed = !rateSnapshot ||
        selects.length !== rateSnapshot.values.length ||
        selects.some(function (s, i) { return s.value !== rateSnapshot.values[i]; });
      if (changed) {
        throw new Error('Destinations changed after the rates were generated. Please generate shipping rates again.');
      }

      var groups = rateSnapshot.groups;
      var canonicalBySource = {};
      groups.forEach(function (group) { canonicalBySource[group.source] = group.canonical; });

      // Every destination line on the page, priced before the cart is touched.
      var destinationItems = groups.map(function (group, index) {
        var rateSelect = document.getElementById('mds-group-select-' + index);
        var noteEl = document.getElementById('mds-gift-note-' + index);
        var dollars = Math.ceil(parseFloat(rateSelect.value));
        if (!Number.isFinite(dollars) || dollars < 0) {
          throw new Error('Please choose a shipping rate for every destination.');
        }
        if (dollars === 0 && !config.freeShippingVariantId) {
          throw new Error('Free shipping is not available for multi-destination orders right now. Please contact us.');
        }
        return {
          id: dollars > 0 ? Number(config.shippingVariantId) : Number(config.freeShippingVariantId),
          quantity: dollars > 0 ? dollars : 1,
          properties: {
            Address: group.canonical,
            _rate: rateSelect.options[rateSelect.selectedIndex].text,
            Note: noteEl ? noteEl.value : ''
          }
        };
      });

      // 1. Clear shipping lines left by an earlier attempt, so a retry can
      //    never charge a destination twice (#79069).
      var cart = await getCart();
      var stale = cart.items.filter(isDestinationLine);
      if (stale.length > 0) {
        var updates = {};
        stale.forEach(function (item) { updates[item.key] = 0; });
        // Removing lines shifts every index after them.
        lineIndicesStale = true;
        cart = await cartRequest('/cart/update.js', { updates: updates });
        if (cart.items.some(isDestinationLine)) {
          throw new Error('Your earlier shipping selections could not be cleared. Please refresh the page and try again.');
        }
      }

      // 2. The cart must still hold exactly the wines on this page.
      var lines = pageLines(selects);
      var cartLines = [];
      cart.items.forEach(function (item, i) {
        if (!isDestinationLine(item)) cartLines.push({ item: item, index: i + 1 });
      });
      var mismatch = cartLines.length !== lines.length || lines.some(function (line, i) {
        return String(cartLines[i].item.variant_id) !== line.variantId ||
          cartLines[i].item.quantity !== line.selects.length;
      });
      if (mismatch) {
        throw new Error('Your cart changed since this page loaded. Please refresh the page and choose destinations again.');
      }

      // 3. Write every unit's destination, one line at a time.
      for (var i = 0; i < lines.length; i++) {
        var index = cartLines[i].index;
        var props = {};
        var existing = cartLines[i].item.properties || {};
        Object.keys(existing).forEach(function (name) {
          if (!/^_Count-\d+$/.test(name)) props[name] = existing[name];
        });
        lines[i].selects.forEach(function (select) {
          props['_Count-' + select.dataset.lineNum] = canonicalBySource[select.value];
          select.dataset.lineItemCount = String(index);
        });
        props._mds_line = String(index);
        cart = await cartRequest('/cart/change.js', {
          line: index,
          quantity: lines[i].selects.length,
          properties: props
        });
      }

      // The page's line indices match the cart again.
      lineIndicesStale = false;

      // 4. Read the units back.
      var written = cart.items.filter(function (item) { return !isDestinationLine(item); });
      var unitsOk = written.length === lines.length && lines.every(function (line, i) {
        var item = written[i];
        return String(item.variant_id) === line.variantId && line.selects.every(function (select) {
          var value = (item.properties || {})['_Count-' + select.dataset.lineNum];
          return addressMatchKey(value) === addressMatchKey(canonicalBySource[select.value]);
        });
      });
      if (!unitsOk) {
        throw new Error('We could not save a destination for every item. Please refresh the page and try again.');
      }

      // 5. All shipping lines in one request.
      lineIndicesStale = true;
      await cartRequest('/cart/add.js', { items: destinationItems });

      // 6. Exactly one shipping line per destination, at the quoted amount.
      cart = await getCart();
      var shippingLines = cart.items.filter(isDestinationLine);
      var linesOk = shippingLines.length === destinationItems.length && destinationItems.every(function (expected) {
        var found = shippingLines.filter(function (item) {
          return item.properties &&
            addressMatchKey(item.properties.Address) === addressMatchKey(expected.properties.Address);
        });
        return found.length === 1 &&
          String(found[0].variant_id) === String(expected.id) &&
          found[0].quantity === expected.quantity;
      });
      if (!linesOk) {
        throw new Error('Shipping could not be added for every destination. Please refresh the page and try again.');
      }

      var liveBtn = document.getElementById('mds-checkout-live-' + config.sectionId);
      if (!liveBtn) throw new Error('Checkout is unavailable. Please refresh the page.');
      leaving = true;
      liveBtn.click();
    } catch (err) {
      console.error('[MDS] Checkout error:', err);
      setCheckoutError(err && err.message ? err.message : 'Something went wrong. Please refresh the page and try again.');
    } finally {
      checkingOut = false;
      if (!leaving) {
        checkoutBtn.disabled = false;
        if (spinner) spinner.style.display = 'none';
      }
    }
  }

  return {
    init: init,
    US_STATES: US_STATES
  };
})();
